import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

// Dev mode: set to true to bypass auth for local UI testing
const DEV_MODE = import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

const MOCK_USER = {
  id: 'dev-user-123',
  email: 'dev@example.com',
  name: 'Dev User',
  role: 'admin' as const,
  avatarUrl: null,
  organizationId: null,
};

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'client';
  avatarUrl: string | null;
  organizationId: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  loginWithMagicLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email: string, password: string) => {
        const response = await api.post<{
          user: User;
          accessToken: string;
          refreshToken: string;
        }>('/auth/login', { email, password });

        set({
          user: response.data.user,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      loginWithMagicLink: async (token: string) => {
        const response = await api.get<{
          user: User;
          accessToken: string;
          refreshToken: string;
        }>(`/auth/verify/${token}`);

        set({
          user: response.data.user,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          await api.post('/auth/logout', { refreshToken });
        } catch {
          // Ignore errors on logout
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await api.post<{
          accessToken: string;
          user: User;
        }>('/auth/refresh', { refreshToken });

        set({
          accessToken: response.data.accessToken,
          user: response.data.user,
        });
      },

      checkAuth: async () => {
        // Dev mode bypass
        if (DEV_MODE) {
          set({
            user: MOCK_USER,
            accessToken: 'dev-token',
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        }

        const { accessToken, refreshToken } = get();

        if (!accessToken && !refreshToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          if (accessToken) {
            const response = await api.get<User>('/auth/me');
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } else if (refreshToken) {
            await get().refreshAccessToken();
            set({ isAuthenticated: true, isLoading: false });
          }
        } catch {
          // Token invalid, clear auth
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'yoda-admin-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
